import AttributeParser from "../utils/AttributeParser";
import { getSharedStyleSheet } from './sharedStyleSheet.js';

class FileBrowser extends HTMLElement {
    static observedAttributes = ['project-id'];

    // Identify the element as a form-associated custom element
    static formAssociated = true;

    constructor() {
        super();
        this.selectedItem = null;
    }

    attributeChangedCallback(attrName, oldValue, newValue) {
        this.dynamicRender();

        if (attrName === 'project-id' && oldValue !== newValue) {
            if (this._childContainer) {
                this._childContainer.setAttribute('project-id', newValue);
                this._childContainer.reset();
                this._childContainer.expand();
            }
        }
    }

    connectedCallback() {
        if (!this.shadowRoot) {
            this.attachShadow({mode: 'open', delegatesFocus: true});

            // Share a single parsed stylesheet across all shadow roots
            getSharedStyleSheet().then(sheet => {
                this.shadowRoot.adoptedStyleSheets = [sheet];
            });

            this._wrapper = document.createElement('div');
            this._wrapper.className = 'file-browser-wrapper';
            this._wrapper.tabIndex = 0;
            this._wrapper.style.overflow = 'auto';
            this._wrapper.style.height = '100%';
            this.shadowRoot.appendChild(this._wrapper);

            // Get access to the internal form control APIs
            this._internals = this.attachInternals();

            const project_id = this.getAttribute('project-id');

            // Add project selector
            if (AttributeParser.parseBooleanAttribute(this.getAttribute('show-project-selector') ?? false)) {
                const projectSelector = `<div class="project-selector-container">Project: <select id="project-selector"></select></div>`;
                this._wrapper.insertAdjacentHTML('beforeend', projectSelector);
                this._projectSelector = this._wrapper.querySelector('#project-selector');

                this.populateProjects(project_id);
            }

            const browserContainer = `<div class="file-browser-container"></div>`;
            this._wrapper.insertAdjacentHTML('beforeend', browserContainer);

            this._container = this._wrapper.querySelector('.file-browser-container');

            const childContainer = `<file-browser-folder class="file-browser-root" file-id="-1" project-id="${project_id}" department-id="-1" path="/" file-title="[project root]"></file-browser-folder>`;
            this._container.insertAdjacentHTML('beforeend', childContainer);

            this._childContainer = this._container.querySelector('file-browser-folder');
            this._rootNode = this._childContainer;
            this._childContainer.setBrowserRoot(this);

            if (AttributeParser.parseBooleanAttribute(this.getAttribute('can-select-folders') ?? true) &&
                AttributeParser.parseBooleanAttribute(this.getAttribute('can-select-root') ?? true)) {
                this._childContainer.setAttribute('selectable', 'true');

                if (AttributeParser.parseBooleanAttribute(this.getAttribute('auto-select-root') ?? false) &&
                    (this.getAttribute('value') ?? '') === '') {
                    this.updateSelection(-1, -1, '/');
                }
            }

            this.setupEvents();
        }

        this.dynamicRender();

        this._childContainer.expand();

        if ((this.getAttribute('value') ?? '') !== '') {
            const json = JSON.parse(this.getAttribute('value'));
            this.updateSelection(json.id ?? -1, json.department_id ?? -1, json.path ?? '/');
        }
    }

    setupEvents() {
        if (AttributeParser.parseBooleanAttribute(this.getAttribute('show-project-selector') ?? false)) {
            this._projectSelector.addEventListener('change', e => {
                const new_project_id = this._projectSelector.value;
                this.setAttribute('project-id', new_project_id);
            });
        }
    }

    dynamicRender() {
        if (!this._wrapper) return;
    }

    updateSelection(id, dept_id, path) {
        if (+id === -1 && path !== '/') {
            const item = this.itemWithPath(path, dept_id);
            if (item) id = item.id;
        }

        this.selectedItem = {file_id: id, dept_id: dept_id, path: path};
        this._rootNode.updateSelection(id, dept_id, path);
        this.updateFormValue();
    }

    async populateProjects(selected_project_id) {
        try {
            const url = `/ajax/file_browse.php?projects`;
            const response = await fetch(url);

            if (!response.ok) {
                throw new Error(`Response status: ${response.status}`);
            }

            const json = await response.json();

            if (json.error) {
                console.log(error);
            } else {
                json.forEach((item) => {
                    const option = `<option value="${item.id}"` + (+item.id === +selected_project_id ? ' selected="selected"' : '') + `>${item.title}</option>`;
                    this._projectSelector.insertAdjacentHTML('beforeend', option);
                });
            }

        } catch (error) {
            console.error(error.message);
        }
    }

    getSelectedItem() {
        if (!this.selectedItem) return null;

        if (this.selectedItem.file_id > 0) {
            return this.itemWithID(this.selectedItem.file_id);
        } else {
            return this.itemWithPath(this.selectedItem.path, this.selectedItem.dept_id);
        }
    }

    itemWithID(id) {
        if (+this._childContainer.getAttribute('file-id') === +id) {
            return {
                id: -1,
                department_id: -1,
                path: '/',
                project_id: this.getAttribute('project-id')
            };
        }

        return this._childContainer.itemWithID(id);
    }

    itemWithPath(path, dept_id) {
        if (+this._childContainer.getAttribute('department-id') === +dept_id &&
            this._childContainer.getAttribute('path') === path) {
            return {
                id: -1,
                department_id: -1,
                path: '/',
                project_id: this.getAttribute('project-id')};
        }

        return this._childContainer.itemWithPath(path, dept_id);
    }

    updateFormValue() {
        if ('undefined' === typeof this._internals) return;

        this._internals.setFormValue(JSON.stringify(this.getSelectedItem()));
    }

    // Form controls usually expose a "value" property
    get value() {
        return JSON.stringify(this.getSelectedItem());
    }

    set value(v) {
        const json = JSON.parse(v);
        this.updateSelection(json.id ?? -1, json.department_id ?? -1, json.path ?? '/');
    }

    // The following properties and methods aren't strictly required,
    // but browser-level form controls provide them. Providing them helps
    // ensure consistency with browser-provided controls.
    get form() {
        return this._internals.form;
    }

    get name() {
        return this.getAttribute('name');
    }

    get type() {
        return this.localName;
    }

    get validity() {
        return this._internals.validity;
    }

    get validationMessage() {
        return this._internals.validationMessage;
    }

    get willValidate() {
        return this._internals.willValidate;
    }

    checkValidity() {
        return this._internals.checkValidity();
    }

    reportValidity() {
        return this._internals.reportValidity();
    }
}

customElements.define('file-browser', FileBrowser);

